<script setup>
// Resultados da lupa da lista: um cartão por MENSAGEM encontrada, com o trecho
// destacado. Clicar abre a conversa já rolando até a mensagem (o
// ConversationView lê ?messageId= e dispara SCROLL_TO_MESSAGE).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { snippetAround } from './conversationListSearchHelpers';

const props = defineProps({
  query: { type: String, default: '' },
  results: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
});
const emit = defineEmits(['loadMore']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { highlightContent } = useMessageFormatter();

const escapeHtml = text => {
  const p = document.createElement('p');
  p.appendChild(document.createTextNode(text || ''));
  return p.innerHTML;
};

const snippetHtml = message => {
  const raw =
    message.content ||
    message.content_attributes?.email?.subject ||
    (message.attachments || []).find(a => a.file_type === 'audio')
      ?.transcribed_text ||
    '';
  return highlightContent(
    escapeHtml(snippetAround(raw, props.query)),
    props.query,
    'searchkey--highlight'
  );
};

const contactName = message =>
  message.conversation?.contact_name ||
  (message.message_type === 0 ? message.sender?.name : '') ||
  t('CHAT_LIST.MESSAGE_SEARCH.UNKNOWN_CONTACT');

const inboxName = message => message.conversation?.inbox_name || '';
const when = message => dynamicTime(message.created_at);
const isOutgoing = message => message.message_type === 1;

const open = message => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: message.conversation_id,
    }),
    { messageId: message.id }
  );
  router.push({ path });
};

const showEmpty = computed(
  () => !props.isLoading && props.results.length === 0
);
</script>

<template>
  <div class="flex-1 overflow-y-auto">
    <p class="px-4 pt-3 pb-1 text-xs text-n-slate-10">
      {{ t('CHAT_LIST.MESSAGE_SEARCH.SCOPE_NOTE') }}
    </p>
    <div
      v-if="showEmpty"
      class="px-4 py-10 text-sm text-center text-n-slate-11"
    >
      {{ t('CHAT_LIST.MESSAGE_SEARCH.NO_RESULTS', { query }) }}
    </div>
    <ul v-else class="m-0 p-0 list-none">
      <li
        v-for="message in results"
        :key="message.id"
        class="border-b border-n-weak"
      >
        <button
          type="button"
          class="flex flex-col w-full gap-1 px-4 py-3 text-left transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:bg-n-alpha-2"
          @click="open(message)"
        >
          <div class="flex items-center justify-between w-full gap-2">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ contactName(message) }}
            </span>
            <span class="text-xs whitespace-nowrap text-n-slate-10">
              {{ when(message) }}
            </span>
          </div>
          <div
            class="flex items-center gap-1.5 w-full min-w-0 text-xs text-n-slate-11"
          >
            <span
              class="flex-shrink-0 size-3.5 text-n-slate-10"
              :class="
                isOutgoing(message)
                  ? 'i-lucide-corner-up-left'
                  : 'i-lucide-corner-down-right'
              "
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span
              class="line-clamp-2 break-words"
              v-html="snippetHtml(message)"
            />
          </div>
          <span v-if="inboxName(message)" class="text-[11px] text-n-slate-9">
            {{ inboxName(message) }}
          </span>
        </button>
      </li>
    </ul>
    <div v-if="isLoading" class="flex justify-center my-4">
      <Spinner class="text-n-brand" />
    </div>
    <div v-else-if="hasMore && results.length" class="flex justify-center py-3">
      <NextButton
        faded
        slate
        sm
        :label="t('CHAT_LIST.MESSAGE_SEARCH.LOAD_MORE')"
        @click="emit('loadMore')"
      />
    </div>
  </div>
</template>
